import React, { createContext, useContext, useMemo, useEffect, useState, useRef } from "react";

import { usePrivy, getAccessToken } from "@privy-io/react-auth";
import * as Sentry from "@sentry/react";
import PropTypes from "prop-types";
import useWebSocketLib, { ReadyState } from "react-use-websocket";

import { config_wsEndpoint } from "src/config";

// Exposes a single shared WebSocket connection per browser tab in Series.
// Consumers read/send via this context instead of opening their own sockets.
const WebSocketContext = createContext(null);

// Provider that owns the single WebSocket instance and a minimal heartbeat watchdog.
export const WebSocketContextProvider = ({ children }) => {
  const { user } = usePrivy();
  const userId = user?.id;
  // Short‑lived Privy JWT attached as a query param for $connect authorizing the user.
  const [authToken, setAuthToken] = useState(null);
  // Switch that we toggle to force a reconnect (used by the heartbeat watchdog).
  const [connectEnabled, setConnectEnabled] = useState(false);
  // Timer used to detect a missed server "pong" after we send a "ping".
  const pongTimerRef = useRef(null);
  // Timer used to re-enable the connect gate after forcing a reconnect.
  const reconnectGateTimerRef = useRef(null);
  // Filtered lastMessage that excludes internal heartbeat messages (ping/pong)
  const [filteredLastMessage, setFilteredLastMessage] = useState(null);
  // Filtered lastJsonMessage that excludes internal heartbeat messages (ping/pong)
  const [filteredLastJsonMessage, setFilteredLastJsonMessage] = useState(null);

  // Obtain a short-lived Privy access token when a user is present.
  // When user changes (login/logout), this refreshes the token driving connection state.
  useEffect(() => {
    let mounted = true;
    if (!userId) {
      setAuthToken(null);
      return;
    }
    getAccessToken()
      .then((token) => {
        if (mounted) setAuthToken(token || null);
      })
      .catch((err) => {
        Sentry.captureException(err, {
          tags  : { component: "WebSocketContext", action: "initialTokenFetch" },
          level : "warning",
        });
        if (mounted) setAuthToken(null);
      });
    return () => {
      mounted = false;
    };
  }, [userId]);

  // We connect only when a token is available AND the gate is enabled.
  // The watchdog briefly disables/enables this to force a clean reconnect.
  const shouldConnect = Boolean(authToken) && connectEnabled;

  // Reconnect policy and heartbeat cadence.
  const BASE_MS = 1000;   // 1s
  const MAX_MS  = 30000;  // 30s cap
  const JITTER  = 0.2;    // ±20%
  const HEARTBEAT_MS = 45000;     // send ping every 45s
  const PONG_TIMEOUT_MS = 12000;  // expect pong within 12s
  const TOKEN_REFRESH_MS = 45 * 60 * 1000; // refresh Privy JWT every 45 minutes

  // react-use-websocket options: token as query param, infinite reconnect with jittered backoff,
  // and a message handler that clears the pending pong timer when a pong arrives.
  const options = useMemo(() => ({
    queryParams       : authToken ? { authToken } : undefined,
    shouldReconnect   : () => true,
    reconnectAttempts : Infinity,
    reconnectInterval : (attemptNumber) => {
      const exp     = Math.min(BASE_MS * (2 ** attemptNumber), MAX_MS);
      const jitter  = Math.floor(exp * JITTER * (Math.random() - 0.5) * 2);
      return Math.max(BASE_MS, exp + jitter);
    },
    onMessage: (e) => {
      try {
        const data = JSON.parse(e?.data || "null");
        if (data && data.type === "pong") {
          if (pongTimerRef.current) {
            clearTimeout(pongTimerRef.current);
            pongTimerRef.current = null;
          }
        }
      }
      catch {
        // ignore non-JSON frames
      }
    }
  }), [authToken]);

  const {
    sendMessage,
    sendJsonMessage,
    lastMessage,
    lastJsonMessage,
    readyState,
  } = useWebSocketLib(config_wsEndpoint, options, shouldConnect);

  // Enable connect whenever we have a token (initial connect).
  // Also used by the watchdog to toggle and force a reconnect on missed pong.
  useEffect(() => {
    setConnectEnabled(Boolean(authToken));
  }, [authToken]);

  // Periodically refresh the Privy access token so reconnections use a fresh credential
  useEffect(() => {
    if (!userId) return undefined;
    const id = setInterval(() => {
      getAccessToken()
        .then(token => setAuthToken(token || null))
        .catch((err) => {
          Sentry.captureException(err, {
            tags  : { component: "WebSocketContext", action: "tokenRefresh" },
            level : "warning",
          });
          setAuthToken(null);
        });
    }, TOKEN_REFRESH_MS);
    return () => {
      clearInterval(id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Heartbeat watchdog: send ping every HEARTBEAT_MS; if no pong in PONG_TIMEOUT_MS, force reconnect.
  useEffect(() => {
    if (readyState !== ReadyState.OPEN) return undefined;

    let mounted = true;

    const id = setInterval(() => {
      try {
        const t = Date.now();
        // Send ping and arm a timeout for the matching pong
        sendJsonMessage({ type: "ping", t });
        if (pongTimerRef.current) clearTimeout(pongTimerRef.current);
        pongTimerRef.current = setTimeout(() => {
          if (!mounted) return;
          setConnectEnabled(false);
          if (reconnectGateTimerRef.current) clearTimeout(reconnectGateTimerRef.current);
          reconnectGateTimerRef.current = setTimeout(() => {
            // Fetch a fresh token before re-enabling the connection gate
            getAccessToken()
              .then((token) => {
                if (mounted) setAuthToken(token || null);
              })
              .catch((err) => {
                Sentry.captureException(err, {
                  tags  : { component: "WebSocketContext", action: "reconnectTokenFetch" },
                  level : "warning",
                });
                if (mounted) setAuthToken(null);
              })
              .finally(() => {
                if (mounted) {
                  setConnectEnabled(true);
                  reconnectGateTimerRef.current = null;
                }
              });
          }, 100);
        }, PONG_TIMEOUT_MS);
      }
      catch (e) {
        // eslint-disable-next-line no-console
        console.debug("[series][ws] ping error", e);
      }
    }, HEARTBEAT_MS);
    return () => {
      mounted = false;
      clearInterval(id);
      if (pongTimerRef.current) {
        clearTimeout(pongTimerRef.current);
        pongTimerRef.current = null;
      }
      if (reconnectGateTimerRef.current) {
        clearTimeout(reconnectGateTimerRef.current);
        reconnectGateTimerRef.current = null;
      }
    };
  }, [readyState, sendJsonMessage]);

  // Filter out internal heartbeat messages (ping/pong) from lastMessage and lastJsonMessage
  // so components don't trigger on them
  useEffect(() => {
    if (!lastMessage?.data) return;

    try {
      const data = JSON.parse(lastMessage.data);
      // Skip internal heartbeat messages
      if (data?.type === "ping" || data?.type === "pong") {
        return;
      }
    }
    catch {
      // If parsing fails, it's not a heartbeat message, so include it
    }

    // This is a real application message, propagate it
    setFilteredLastMessage(lastMessage);
    setFilteredLastJsonMessage(lastJsonMessage);
  }, [lastMessage, lastJsonMessage]);

  // Values exposed to consumers (senders + latest frames + ready state)
  // Use filtered versions to exclude heartbeat messages from triggering re-renders
  const value = useMemo(() => ({
    sendMessage,
    sendJsonMessage,
    lastMessage     : filteredLastMessage,
    lastJsonMessage : filteredLastJsonMessage,
    readyState,
  }), [sendMessage, sendJsonMessage, filteredLastMessage, filteredLastJsonMessage, readyState]);

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};

WebSocketContextProvider.propTypes = {
  children: PropTypes.node,
};

export function useWebSocketContext() {
  const ctx = useContext(WebSocketContext);
  if (!ctx) {
    throw new Error("useWebSocketContext must be used within WebSocketContextProvider");
  }
  return ctx;
}
